import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import {
  type AcceptCashTransfer,
  type AddCashBookEntry,
  type AddMainDesks,
  type CashReportDraft,
  type CloseCashMonth,
  type CreateCashDesk,
  type PayCash,
  type RefuseCashTransfer,
  type SavePrinter,
  type SaveTerminal,
  type SendCashTransfer,
  type SetCashBookDate,
  type SetCashDeskSeries,
  type SetCashierPaymentType,
  type SubmitCashReport,
  type CashierThresholds,
  type UpdateCashBookEntry,
  type UpdateCashDesk,
  cashBook,
  cashDesk,
  cashDocumentLines,
  cashFoundDocument,
  cashierHome,
  cashierSettings,
  cashMonth,
  cashOutstanding,
  cashOverview,
  cashPayoutMonth,
  cashPendingReports,
  cashReport,
  cashTransfer,
} from '@overseer/contracts';
import { request } from '@/lib/api/client';

/**
 * Prefix shared by every cashier read. A write moves cash between figures on
 * several screens at once — a report's balance, the outstanding handover, the
 * main desk's book — so after one the caller refreshes the whole module with
 * `refresh(client, CASHIER_KEY_PREFIX)`.
 */
export const CASHIER_KEY_PREFIX = ['cashier'] as const;

const created = z.object({ id: z.int() });

/** The desks the signed-in user acts on; every screen's desk picker. */
export const cashierHomeQuery = () =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'home'] as const,
    queryFn: ({ signal }) => request('/v1/cashier/home', cashierHome, { signal }),
    staleTime: 30 * 1000,
  });

export const cashReportQuery = (desk: number, date: string) =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'report', desk, date] as const,
    queryFn: ({ signal }) =>
      request('/v1/cashier/report', cashReport, { signal, searchParams: { desk, date } }),
    staleTime: 30 * 1000,
  });

/** Submitted reports at the desks the user accepts, oldest day first: what waits for the desk manager in Historia raportów. */
export const cashPendingReportsQuery = () =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'pending'] as const,
    queryFn: ({ signal }) => request('/v1/cashier/reports/pending', cashPendingReports, { signal }),
    staleTime: 30 * 1000,
  });

export const cashMonthQuery = (desk: number, year: number, month: number) =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'month', desk, year, month] as const,
    queryFn: ({ signal }) =>
      request('/v1/cashier/month', cashMonth, { signal, searchParams: { desk, year, month } }),
    staleTime: 30 * 1000,
  });

/** `q`: a document's number or whole parts of it, `1/10`, or its copied name. */
export type CashDocumentSearch = {
  desk: number;
  q: string;
};

/** Documents by number, of any series of the desk's sources and from outside the day's window too — to pull one in. */
export const cashDocumentsQuery = (search: CashDocumentSearch) =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'documents', search] as const,
    queryFn: ({ signal }) =>
      request('/v1/cashier/documents', z.array(cashFoundDocument), { signal, searchParams: search }),
    staleTime: 60 * 1000,
  });

/**
 * One document's lines, read through the desk it belongs to. Fetched when its
 * row first opens and kept a while, so folding and opening it again reads nothing.
 */
export const cashDocumentLinesQuery = (desk: number, sourceId: number, documentId: string) =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'document-lines', desk, sourceId, documentId] as const,
    queryFn: ({ signal }) =>
      request(`/v1/cashier/documents/${sourceId}/${encodeURIComponent(documentId)}/lines`, cashDocumentLines, {
        signal,
        searchParams: { desk },
      }),
    staleTime: 5 * 60 * 1000,
  });

export type CashTransferFilter = {
  desk?: number;
  /** With `desk`: only the transfers between the two. */
  with?: number;
  state?: 'sent' | 'accepted';
  from?: string;
  to?: string;
};

export const cashTransfersQuery = (filter: CashTransferFilter) =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'transfers', filter] as const,
    queryFn: ({ signal }) =>
      request('/v1/cashier/transfers', z.array(cashTransfer), { signal, searchParams: filter }),
    staleTime: 30 * 1000,
  });

export const cashOutstandingQuery = (desk: number) =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'outstanding', desk] as const,
    queryFn: ({ signal }) => request(`/v1/cashier/outstanding/${desk}`, cashOutstanding, { signal }),
    staleTime: 30 * 1000,
  });

export const cashBookQuery = (desk: number, year: number, month: number) =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'book', desk, year, month] as const,
    queryFn: ({ signal }) =>
      request('/v1/cashier/book', cashBook, { signal, searchParams: { desk, year, month } }),
    staleTime: 30 * 1000,
  });

/** A stash B desk's cash pay for one payroll month: what is left to pay or hand back, what was paid, what waits for "Gotowe". */
export const cashPayoutsQuery = (desk: number, year: number, month: number) =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'payouts', desk, year, month] as const,
    queryFn: ({ signal }) =>
      request('/v1/cashier/payouts', cashPayoutMonth, { signal, searchParams: { desk, year, month } }),
    staleTime: 30 * 1000,
  });

/** The settings. Read with `cashier.read` or `cashier.settings`; anyone else gets a 403. */
export const cashierSettingsQuery = () =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'settings'] as const,
    queryFn: ({ signal }) => request('/v1/cashier/settings', cashierSettings, { signal }),
    staleTime: 60 * 1000,
  });

/** Every main desk's year, month by month: opening, in, out, closing. */
export const cashOverviewQuery = (year: number) =>
  queryOptions({
    queryKey: [...CASHIER_KEY_PREFIX, 'overview', year] as const,
    queryFn: ({ signal }) => request('/v1/cashier/overview', cashOverview, { signal, searchParams: { year } }),
    staleTime: 60 * 1000,
  });

// ── The daily report ────────────────────────────────────────────────────────

/** Saves the draft; the answer is the report recomputed from it. */
export const saveCashDraftRequest = (desk: number, date: string, draft: CashReportDraft) =>
  request(`/v1/cashier/report/${desk}/${date}/draft`, cashReport, { method: 'PUT', body: draft });

/**
 * Submits the day with what goes with it: the comment and the handover to the
 * main desk, which the submit makes as a transfer for the main cashier to
 * accept. A null handover hands nothing over.
 */
export const submitCashReportRequest = (desk: number, date: string, body: SubmitCashReport) =>
  request(`/v1/cashier/report/${desk}/${date}/submit`, cashReport, { method: 'POST', body });

export const acceptCashReportRequest = (id: number) =>
  request(`/v1/cashier/reports/${id}/accept`, z.void(), { method: 'POST' });

export const returnCashReportRequest = (id: number, reason: string) =>
  request(`/v1/cashier/reports/${id}/return`, z.void(), { method: 'POST', body: { reason } });

export const reopenCashReportRequest = (id: number, reason: string) =>
  request(`/v1/cashier/reports/${id}/reopen`, z.void(), { method: 'POST', body: { reason } });

export const addCashCommentRequest = (id: number, text: string) =>
  request(`/v1/cashier/reports/${id}/comments`, z.void(), { method: 'POST', body: { text } });


// ── Transfers ────────────────────────────────────────────────────────────────

export const sendCashTransferRequest = (body: SendCashTransfer) =>
  request('/v1/cashier/transfers', cashTransfer, { method: 'POST', body });

export const acceptCashTransferRequest = (id: number, body: AcceptCashTransfer) =>
  request(`/v1/cashier/transfers/${id}/accept`, cashTransfer, { method: 'POST', body });

/** The main cashier sends a day back to its cashier: its handover's count did not match. */
export const returnTransferDayRequest = (id: number, reason: string) =>
  request(`/v1/cashier/transfers/${id}/return-day`, z.void(), { method: 'POST', body: { reason } });

/** Cash sent by hand that does not count up goes back to its sender, who sends it again. */
export const refuseCashTransferRequest = (id: number, body: RefuseCashTransfer) =>
  request(`/v1/cashier/transfers/${id}/refuse`, z.void(), { method: 'POST', body });

/** The day a transfer stands under in one main desk's book. */
export const setTransferBookDateRequest = (id: number, body: SetCashBookDate) =>
  request(`/v1/cashier/transfers/${id}/book-date`, z.void(), { method: 'PUT', body });

// ── The main desk ────────────────────────────────────────────────────────────

export const addCashBookEntryRequest = (body: AddCashBookEntry) =>
  request('/v1/cashier/book/entries', z.void(), { method: 'POST', body });

/** A hand-entered line, replaced whole. */
export const updateCashBookEntryRequest = (id: number, body: UpdateCashBookEntry) =>
  request(`/v1/cashier/book/entries/${id}`, z.void(), { method: 'PUT', body });

export const deleteCashBookEntryRequest = (id: number) =>
  request(`/v1/cashier/book/entries/${id}`, z.void(), { method: 'DELETE' });

export const closeCashMonthRequest = (desk: number, body: CloseCashMonth) =>
  request(`/v1/cashier/book/${desk}/close`, z.void(), { method: 'POST', body });

export const reopenCashMonthRequest = (desk: number, year: number, month: number) =>
  request(`/v1/cashier/book/${desk}/reopen`, z.void(), { method: 'POST', searchParams: { year, month } });

// ── Cash payouts from payroll ───────────────────────────────────────────────

/** Pays — or takes back — the parts chosen of one payroll row; `total` is what the screen showed. */
export const payCashRequest = (body: PayCash) =>
  request('/v1/cashier/payouts', z.void(), { method: 'POST', body });

/** The day a payout stands under in its desk's book. */
export const setPayoutDateRequest = (id: number, body: SetCashBookDate) =>
  request(`/v1/cashier/payouts/${id}/date`, z.void(), { method: 'PUT', body });

/** Undoes a payout of an open month; its line leaves the book. */
export const undoPayoutRequest = (id: number) =>
  request(`/v1/cashier/payouts/${id}`, z.void(), { method: 'DELETE' });

// ── Settings ─────────────────────────────────────────────────────────────────

/** A company's two main desks, stash A and stash B. The company is kept on Administracja → Firmy. */
export const addMainDesksRequest = (body: AddMainDesks) =>
  request('/v1/cashier/settings/main-desks', z.array(cashDesk), { method: 'POST', body });

/** A sales desk; main desks are added for a company. */
export const createCashDeskRequest = (body: CreateCashDesk) =>
  request('/v1/cashier/settings/desks', cashDesk, { method: 'POST', body });

export const updateCashDeskRequest = (id: number, body: UpdateCashDesk) =>
  request(`/v1/cashier/settings/desks/${id}`, z.void(), { method: 'PATCH', body });

/** The full set, of sources on the desk; a series bound to another desk moves here. */
export const setCashDeskSeriesRequest = (id: number, body: SetCashDeskSeries) =>
  request(`/v1/cashier/settings/desks/${id}/series`, z.void(), { method: 'PUT', body });

export const createPrinterRequest = (deskId: number, body: SavePrinter) =>
  request(`/v1/cashier/settings/desks/${deskId}/printers`, created, { method: 'POST', body });

export const updatePrinterRequest = (id: number, body: SavePrinter) =>
  request(`/v1/cashier/settings/printers/${id}`, z.void(), { method: 'PUT', body });

export const createTerminalRequest = (deskId: number, body: SaveTerminal) =>
  request(`/v1/cashier/settings/desks/${deskId}/terminals`, created, { method: 'POST', body });

export const updateTerminalRequest = (id: number, body: SaveTerminal) =>
  request(`/v1/cashier/settings/terminals/${id}`, z.void(), { method: 'PUT', body });

/** Puts a data source on a sales desk; its payment mapping is copied from the desk that changed one of that source last. */
export const addDeskSourceRequest = (deskId: number, sourceId: number) =>
  request(`/v1/cashier/settings/desks/${deskId}/sources/${sourceId}`, z.void(), { method: 'PUT' });

/** Takes a data source off a desk, with its series and payment mapping. */
export const removeDeskSourceRequest = (deskId: number, sourceId: number) =>
  request(`/v1/cashier/settings/desks/${deskId}/sources/${sourceId}`, z.void(), { method: 'DELETE' });

/** What a payment type of one of the desk's sources is to the desk's drawer. */
export const setDeskPaymentTypeRequest = (deskId: number, sourceId: number, type: string, body: SetCashierPaymentType) =>
  request(`/v1/cashier/settings/desks/${deskId}/sources/${sourceId}/payment-types/${encodeURIComponent(type)}`, z.void(), {
    method: 'PUT',
    body,
  });

export const setThresholdsRequest = (body: CashierThresholds) =>
  request('/v1/cashier/settings/thresholds', z.void(), { method: 'PUT', body });

